import { type HTMLComponent, useDebounce } from "@commercekit/react";

import type {
  CustomField,
  ProductAttributeSearchFilter,
  WovarCategory,
  WovarCustomer,
} from "@commercekit/api";
import { useCustomerSimple, useLocalePath } from "@commercekit/remix";
import { PrismicImage } from "@prismicio/react";
import { useLocation, useNavigate, useNavigation } from "@remix-run/react";
import * as Sentry from "@sentry/remix";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useWvViewItemList, useWvSearchFilters } from "~/hooks/useDataLayer";
import { useFeatureToggle } from "~/hooks/useFeatureToggle";
import { CATEGORY_PAGINATION_TOGGLE } from "~/lib/featureToggle";
import { useLoadMoreProducts } from "~/hooks/useLoadMoreProducts";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import type { FaqSliceSlice, RichTextSlice } from "prismicio-types";
import type { CategoryPageResultType } from "~/routes-server/$slug/types";
import WvBreadcrumbs from "../atoms/WvBreadcrumbs";
import WvLink from "../atoms/WvLink";
import WvSearchFilter from "../organisms/WvSearchFilter";
import WvCategoryQuickMobileNav from "../molecules/WvCategoryQuickMobileNav";
import { usePrevious } from "react-use";

const WvProductListPage: HTMLComponent<{
  categoryPageResult: CategoryPageResultType;
}> = ({ categoryPageResult }) => {

  const {
    products,
    pagination,
    filters,
    availableFilters,
    highlightedFilter,
    categoryData,
    categoryBreadcrumbs,
    uspBarImageAndTextSlices,
    categoryDocument,
    emptyProductListImage,
    categoriesMobileNav,
  } = categoryPageResult;

  const { customer } = useCustomerSimple();
  const { state, location } = useNavigation();
  const currentLocation = useLocation();
  const localePath = useLocalePath();
  const { $pt } = usePrismicLabels();
  const gtmViewItemList = useWvViewItemList();

  const pageLoading = useMemo(
    () => state === "loading" && location.pathname === currentLocation.pathname,
    [currentLocation.pathname, location?.pathname, state]
  );

  // WOV-2195: the A/B variant swaps infinite scroll for numbered page links.
  const isPaginationEnabled = useFeatureToggle(CATEGORY_PAGINATION_TOGGLE, false);
  const currentPage = useMemo(() => {
    const parsed = Number.parseInt(
      new URLSearchParams(currentLocation.search).get("page") || "",
      10
    );
    return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
  }, [currentLocation.search]);

  const {
    exhibitProduct,
    exhibitFilter,
    loadMoreLoading,
    loadRef,
  } = useLoadMoreProducts(
    products,
    categoryData?.id as string,
    availableFilters,
    pagination,
    !isPaginationEnabled
  );

  const [newFilters, setNewFilters] = useState<CustomField[]>([]);
  const richTextSlices = categoryDocument?.data?.slices?.filter(
    (slice) => slice?.slice_type === "rich_text"
  ) as RichTextSlice[];

  const faqSlice = categoryDocument?.data.slices.filter((slice) => {
    return (
      slice.slice_type === "faq_slice" && slice.variation === "categoryFaq"
    );
  }) as FaqSliceSlice[];

  useEffect(() => {
    if (products.length > 0) {
      setNewFilters(exhibitFilter);
    }
  }, [products, exhibitFilter]);

  const filterCountMap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    for (const group of filters ?? []) {
      if (!group.name || !group.options) continue;
      map[group.name] = {};
      for (const opt of group.options) {
        map[group.name][opt.value] = opt.count;
      }
    }
    return map;
  }, [filters]);

  let mappedFilters: ProductAttributeSearchFilter[] = newFilters
    .filter(
      (i) =>
        i.displayName && i.value.stringValues && i.value.stringValues.length
    )
    .map(({ name, displayName, value }) => ({
      name: displayName,
      options: value.stringValues.map((o) => ({
        name: displayName,
        key: name,
        value: o,
        count: filterCountMap[name]?.[o] ?? 0,
      })),
    }));

  const normalizedHighlightedFilter = useMemo(() => {
    if (!highlightedFilter) return { name: "", options: [] };
    return {
      name: highlightedFilter.displayName,
      options: highlightedFilter.value.stringValues.map((o) => ({
        name: o,
        key: highlightedFilter.name,
        value: o,
        count: filterCountMap[highlightedFilter.displayName]?.[o] ?? 0,
      })),
    };
  }, [highlightedFilter, filterCountMap]);

  useMemo(() => {
    gtmViewItemList(exhibitProduct);
  }, [gtmViewItemList, exhibitProduct]);

  useEffect(() => {
    if (
      pagination?.total !== undefined &&
      pagination.total > 1 &&
      exhibitProduct.length === 1
    ) {
      Sentry.captureMessage(
        "PLP: only one product rendered despite multiple in pagination",
        {
          level: "warning",
          fingerprint: ["plp-single-product-bug"],
          tags: { kind: "diagnostic" },
          extra: {
            totalInPagination: pagination.total,
            renderedCount: exhibitProduct.length,
            categoryId: categoryData?.id,
            categoryName: categoryData?.name,
            url: typeof window !== "undefined" ? window.location.href : undefined,
          },
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination?.total, exhibitProduct.length, categoryData?.id]);

  const hasFilters = useMemo(() => {
    return currentLocation.search.includes("filter");
  }, [currentLocation]);

  useEffect(() => {
    if (window.yotpoWidgetsContainer?.initWidgets) {
      setTimeout(() => {
        window.yotpoWidgetsContainer.initWidgets?.();
      }, 15);
    }
  }, [exhibitProduct])

  const gtmSearchFilters = useWvSearchFilters();
  const gtmSearchFiltersParamsRef = useRef<Parameters<typeof gtmSearchFilters>[0] | null>(null);
  const debouncedGtmSearchFilters = useDebounce(() => {
    const params = gtmSearchFiltersParamsRef.current;
    if (params) {
      gtmSearchFiltersParamsRef.current = null;
      gtmSearchFilters(params);
    }
  }, 1000);
  const prevSearch = usePrevious(currentLocation.search);

  useEffect(() => {
    if (currentLocation.search === prevSearch) return;
    gtmSearchFiltersParamsRef.current = {
      currentSearch: currentLocation.search,
      previousSearch: prevSearch,
      resultsCount: pagination?.total ?? 0,
      categoryName: categoryData?.name || "",
    };
    debouncedGtmSearchFilters();
  }, [
    currentLocation.search,
    prevSearch,
    categoryData?.name,
    pagination?.total,
    debouncedGtmSearchFilters,
  ]);

  const navigate = useNavigate();
  // Instead of using the browser history, we use a custom history in case user is coming from an external source
  // So that user isn't taken out of the app and at the same time, still preserve last valid search.
  const [filterHistory, setFilterHistory] = useState<string[]>([]);
  useEffect(() => {
    setFilterHistory(prev => {
      if (prev[prev.length - 1] !== currentLocation.search) {
        return [...prev, currentLocation.search];
      }
      return prev;
    });
  }, [currentLocation.search]);

  const rollback = () => {
    const lastValidSearch =
      [...filterHistory]
        .reverse()
        .slice(1)
        .find(Boolean) ?? "";
    navigate({
      pathname: currentLocation.pathname,
      search: lastValidSearch,
    });
  };
  const backComponent = () => {
    return hasFilters ? (
      <button
        onClick={() => {
          rollback();
        }}
        className="d-inline-block btn btn--primary mt-3"
      >
        {$pt("back")}
      </button>
    ) : (
      <WvLink
        to={localePath("/")}
        className="d-inline-block btn btn--primary mt-3"
      >
        {$pt("return_to_home_page")}
      </WvLink>
    );
  }

  return (
    <>
      {exhibitProduct && exhibitProduct.length > 0 ? (
        <div
          className={classNames(
            "productlist",
            pageLoading || loadMoreLoading ? "productlist--loading" : ""
          )}
        >
          <div className="container">
            {categoriesMobileNav?.length > 0 && (
              <WvCategoryQuickMobileNav items={categoriesMobileNav} />
            )}
            {categoryData?.breadcrumbs && (
              <WvBreadcrumbs breadcrumbs={categoryBreadcrumbs} />
            )}
            <WvSearchFilter
              filters={mappedFilters}
              quickMobileFilters={normalizedHighlightedFilter}
              products={exhibitProduct}
              isProductLoading={pageLoading}
              totalProductCount={pagination?.total as number}
              uspBarImageAndTextSlices={uspBarImageAndTextSlices}
              richTextSlices={richTextSlices}
              faqSlice={faqSlice}
              categoryData={categoryData as WovarCategory}
              loadRef={loadRef}
              customer={customer as WovarCustomer}
              usePagination={isPaginationEnabled}
              currentPage={currentPage}
              prioritizeFirstImages
            />
          </div>
        </div>
      ) : (
        <div className="container mt-3 mt-lg-5 mb-5 pb-lg-5 text-center">
          <div className="content">
            <PrismicImage
              field={emptyProductListImage}
              loading="lazy"
              width={351}
              height={159}
              className="mb-3 px-5 mx-auto h-auto"
              style={{ maxWidth: "361px" }}
            />
          </div>

          <h1 className="heading xxl">
            {$pt(
              hasFilters ? "empty_filtered_product_list" : "empty_product_list"
            )}
          </h1>
          <div className="content">
            {backComponent()}
          </div>
        </div>
      )}
    </>
  );
};
export default WvProductListPage;
